import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Upload, Plus, Minus, X, Loader2, ShoppingCart, Wand2, LayoutGrid, FileWarning, Palette, Sparkles } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import Layout from "@/components/layout/Layout";
import { GreenButton, GhostButton } from "@/components/Buttons";
import { useCart } from "@/context/CartContext";
import LiveDesigner from "@/components/LiveDesigner";

const cx = (...c) => c.filter(Boolean).join(" ");

function defaultSelections(product) {
    const sel = {};
    for (const g of product.option_groups) {
        if (g.type === "choice") sel[g.key] = (g.options?.find((o) => o.value === "both") || g.options[0]).value;
        else if (g.type === "swatch") sel[g.key] = g.options[0].value;
        else if (g.type === "quantity") sel[g.key] = g.min || 1;
        else if (g.type === "dimensions") sel[g.key] = g.min || 1;
        else if (g.type === "sizes") sel[g.key] = {};
        else if (g.type === "jobs") sel[g.key] = [{ quantity: 1, width: "", length: "", file_name: "" }];
        else if (g.type === "upload") sel[g.key] = [];
        else sel[g.key] = "";
    }
    return sel;
}

function groupComplete(g, sel) {
    const v = sel[g.key];
    if (g.type === "sizes") return Object.values(v || {}).reduce((a, b) => a + (+b || 0), 0) > 0;
    if (g.type === "dimensions") return (+v || 0) >= (g.min || 1);
    if (g.type === "quantity") return (+v || 0) >= (g.min || 1);
    if (g.type === "jobs") return (v || []).some((j) => +j.quantity > 0);
    if (g.type === "upload" || g.type === "date" || g.type === "notes") return true; // optional
    return !!v;
}

function summarize(product, sel) {
    const parts = [];
    for (const g of product.option_groups) {
        const v = sel[g.key];
        if (g.type === "choice" || g.type === "swatch") {
            const opt = g.options.find((o) => o.value === v);
            if (opt) parts.push(opt.label);
        } else if (g.type === "sizes") {
            const s = Object.entries(v || {}).filter(([, q]) => +q > 0).map(([k, q]) => `${q}${k}`).join(" ");
            if (s) parts.push(s);
        } else if (g.type === "dimensions") parts.push(`${v} ${g.unit}`);
        else if (g.type === "quantity") parts.push(`Qty ${v}`);
        else if (g.type === "jobs") parts.push(`${(v || []).length} job(s)`);
    }
    return parts.join(" · ");
}

/* ---------------- Field renderers ---------------- */
function ChoiceField({ g, value, onChange }) {
    return (
        <div className="grid gap-3 sm:grid-cols-3">
            {g.options.map((o) => {
                const active = value === o.value;
                return (
                    <button key={o.value} onClick={() => onChange(o.value)} data-testid={`opt-${g.key}-${o.value}`}
                        className={cx("group rounded-2xl border p-4 text-left transition-all duration-200",
                            active ? "border-brand-green bg-brand-green/10 glow-green" : "border-white/12 bg-white/[0.03] hover:border-white/30")}>
                        <div className="flex items-center justify-between">
                            <span className="font-semibold">{o.label}</span>
                            <span className={cx("flex h-5 w-5 items-center justify-center rounded-full border", active ? "border-brand-green bg-brand-green text-black" : "border-white/30")}>
                                {active && <Check size={13} />}
                            </span>
                        </div>
                        {o.desc && <p className="mt-1 text-xs text-zinc-400">{o.desc}</p>}
                    </button>
                );
            })}
        </div>
    );
}

function SwatchField({ g, value, onChange }) {
    return (
        <div className="flex flex-wrap gap-3">
            {g.options.map((o) => {
                const active = value === o.value;
                return (
                    <button key={o.value} onClick={() => onChange(o.value)} data-testid={`swatch-${o.value}`} title={o.label}
                        className={cx("flex flex-col items-center gap-1.5 rounded-xl p-1 transition-transform duration-200 hover:scale-105")}>
                        <span className={cx("h-11 w-11 rounded-full border-2", active ? "border-brand-green" : "border-white/20")} style={{ background: o.hex }} />
                        <span className={cx("text-[11px]", active ? "text-brand-green" : "text-zinc-400")}>{o.label}</span>
                    </button>
                );
            })}
        </div>
    );
}

function Stepper({ value, onChange, min = 0 }) {
    return (
        <div className="inline-flex items-center overflow-hidden rounded-full border border-white/15">
            <button onClick={() => onChange(Math.max(min, (+value || 0) - 1))} className="px-3 py-2 text-zinc-300 hover:bg-white/10"><Minus size={15} /></button>
            <span className="min-w-10 text-center font-mono font-bold">{value}</span>
            <button onClick={() => onChange((+value || 0) + 1)} className="px-3 py-2 text-zinc-300 hover:bg-white/10"><Plus size={15} /></button>
        </div>
    );
}

function SizesField({ g, value, onChange }) {
    const set = (size, q) => onChange({ ...value, [size]: Math.max(0, q) });
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {g.sizes.map((s) => (
                <div key={s} className={cx("rounded-xl border p-3 text-center transition-colors", +value[s] > 0 ? "border-brand-green/60 bg-brand-green/5" : "border-white/12 bg-white/[0.03]")} data-testid={`size-${s}`}>
                    <div className="mb-2 font-display text-lg font-bold">{s}</div>
                    <div className="flex justify-center"><Stepper value={value[s] || 0} onChange={(q) => set(s, q)} /></div>
                </div>
            ))}
        </div>
    );
}

function JobsField({ g, value, onChange }) {
    const update = (i, k, v) => { const n = [...value]; n[i] = { ...n[i], [k]: v }; onChange(n); };
    const add = () => onChange([...value, { quantity: 1, width: "", length: "", file_name: "" }]);
    const remove = (i) => onChange(value.filter((_, x) => x !== i));
    return (
        <div className="space-y-3">
            {value.map((job, i) => (
                <div key={i} className="rounded-xl border border-white/12 bg-white/[0.03] p-4" data-testid={`job-row-${i}`}>
                    <div className="mb-3 flex items-center justify-between">
                        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Job #{i + 1}</span>
                        {value.length > 1 && <button onClick={() => remove(i)} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <label className="text-xs text-zinc-400">Qty<input type="number" min="1" value={job.quantity} onChange={(e) => update(i, "quantity", e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-brand-ink px-3 py-2 text-white" /></label>
                        <label className="text-xs text-zinc-400">Width<input value={job.width} onChange={(e) => update(i, "width", e.target.value)} placeholder='e.g. 4"' className="mt-1 w-full rounded-lg border border-white/15 bg-brand-ink px-3 py-2 text-white" /></label>
                        <label className="text-xs text-zinc-400">Length<input value={job.length} onChange={(e) => update(i, "length", e.target.value)} placeholder='e.g. 6"' className="mt-1 w-full rounded-lg border border-white/15 bg-brand-ink px-3 py-2 text-white" /></label>
                        <label className="text-xs text-zinc-400">File name<input value={job.file_name} onChange={(e) => update(i, "file_name", e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-brand-ink px-3 py-2 text-white" /></label>
                    </div>
                </div>
            ))}
            <GhostButton onClick={add} className="w-full py-3"><Plus size={16} /> Add another job</GhostButton>
        </div>
    );
}

function UploadField({ g, value, onChange }) {
    const [busy, setBusy] = useState(false);
    const ref = useRef();
    const handle = async (files) => {
        setBusy(true);
        try {
            const uploaded = [];
            for (const f of files) {
                const fd = new FormData(); fd.append("file", f);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data);
            }
            onChange([...(value || []), ...uploaded]);
            toast.success(`${uploaded.length} file(s) uploaded`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div>
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                <FileWarning size={16} className="mt-0.5 flex-shrink-0" /> We print files exactly as submitted. No editing or redesign. Need cleanup? Submit an Artwork Request first.
            </div>
            <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`upload-${g.key}`}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/20 bg-white/[0.02] py-8 transition-colors hover:border-brand-green/50">
                {busy ? <Loader2 className="animate-spin text-brand-green" /> : <Upload className="text-brand-green" />}
                <span className="text-sm font-semibold">{busy ? "Uploading…" : "Tap to upload artwork"}</span>
                <span className="text-xs text-zinc-500">{g.help || ".ai .pdf .png .jpg · max 50MB"}</span>
            </button>
            <input ref={ref} type="file" multiple={g.multiple} className="hidden" onChange={(e) => handle(Array.from(e.target.files))} accept=".ai,.pdf,.png,.jpg,.jpeg,.svg,.eps,.psd,.webp" />
            {(value || []).length > 0 && (
                <div className="mt-3 space-y-2">
                    {value.map((f, i) => (
                        <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
                            <span className="truncate">{f.filename}</span>
                            <button onClick={() => onChange(value.filter((_, x) => x !== i))} className="text-zinc-500 hover:text-red-400"><X size={15} /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function DimensionsField({ g, value, onChange }) {
    return (
        <div className="flex flex-wrap items-center gap-4">
            <div className="rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3">
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Width</span>
                <div className="font-display text-xl font-bold">{g.fixed_width}</div>
            </div>
            <div className="rounded-xl border border-brand-green/40 bg-brand-green/5 px-4 py-3">
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Length ({g.unit})</span>
                <div className="mt-1"><Stepper value={value} min={g.min || 1} onChange={onChange} /></div>
            </div>
        </div>
    );
}

function Field({ g, sel, update }) {
    const value = sel[g.key];
    const set = (v) => update(g.key, v);
    if (g.type === "choice") return <ChoiceField g={g} value={value} onChange={set} />;
    if (g.type === "swatch") return <SwatchField g={g} value={value} onChange={set} />;
    if (g.type === "sizes") return <SizesField g={g} value={value} onChange={set} />;
    if (g.type === "quantity") return <Stepper value={value} min={g.min || 1} onChange={set} />;
    if (g.type === "dimensions") return <DimensionsField g={g} value={value} onChange={set} />;
    if (g.type === "jobs") return <JobsField g={g} value={value} onChange={set} />;
    if (g.type === "upload") return <UploadField g={g} value={value} onChange={set} />;
    if (g.type === "date") return <input type="date" value={value} onChange={(e) => set(e.target.value)} data-testid="field-date" className="rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white" />;
    if (g.type === "notes") return <textarea value={value} onChange={(e) => set(e.target.value)} rows={3} data-testid="field-notes" placeholder="Any specific colors, sizes, or instructions…" className="w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white" />;
    return null;
}

/* ---------------- Summary pane ---------------- */
function SummaryPane({ product, sel, estimate, onAdd, adding }) {
    return (
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
            <img src={product.image} alt={product.name} className="mb-4 h-40 w-full rounded-2xl object-cover" onError={(e) => (e.target.style.display = "none")} />
            <h3 className="font-display text-xl font-bold">{product.name}</h3>
            <p className="mt-1 text-xs text-zinc-400">{summarize(product, sel) || "Make your selections…"}</p>
            <div className="my-4 h-px bg-white/10" />
            <div className="flex items-end justify-between">
                <div>
                    <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Estimated total</span>
                    <div className="font-display text-3xl font-black text-brand-green">{estimate ? `$${estimate.line_total.toFixed(2)}` : "—"}</div>
                    {estimate?.quantity ? <span className="text-xs text-zinc-500">{estimate.quantity} × ${estimate.unit_price.toFixed(2)}</span> : null}
                </div>
            </div>
            <p className="mt-3 text-[11px] text-zinc-500">Estimate only. Our team confirms final pricing & payment (Venmo / Zelle / Cash App / Cash). Order starts once paid in full.</p>
            {sel.design_preview && (
                <div className="mt-3 flex items-center gap-3 rounded-xl border border-brand-green/30 bg-brand-green/5 p-2" data-testid="summary-design-preview">
                    <img src={sel.design_preview.id ? fileUrl(sel.design_preview.id) : undefined} alt="design" className="h-12 w-12 rounded-lg object-cover" onError={(e) => (e.target.style.display = "none")} />
                    <span className="text-xs text-brand-green">Live design attached</span>
                </div>
            )}
            {(sel.artwork?.length || 0) > 0 && !sel.design_preview && (
                <p className="mt-3 text-xs text-brand-green" data-testid="summary-artwork-count">{sel.artwork.length} file(s) attached</p>
            )}
            <GreenButton className="mt-4 w-full" onClick={onAdd} disabled={adding} data-testid="add-to-cart-button">
                {adding ? <Loader2 className="animate-spin" size={16} /> : <ShoppingCart size={16} />} Add to Cart
            </GreenButton>
        </div>
    );
}

/* ---------------- Main ---------------- */
export default function OrderStudio() {
    const { slug } = useParams();
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const { addItem, setOpen } = useCart();
    const [product, setProduct] = useState(null);
    const [mode, setMode] = useState(sp.get("mode") === "pro" ? "pro" : "wizard");
    const [sel, setSel] = useState(null);
    const [step, setStep] = useState(0);
    const [estimate, setEstimate] = useState(null);
    const [adding, setAdding] = useState(false);
    const [showDesigner, setShowDesigner] = useState(false);

    useEffect(() => {
        api.get(`/products/${slug}`).then(({ data }) => { setProduct(data); setSel(defaultSelections(data)); setStep(0); })
            .catch(() => { toast.error("Product not found"); navigate("/directory"); });
    }, [slug, navigate]);

    const update = useCallback((key, value) => setSel((s) => ({ ...s, [key]: value })), []);

    const handleUseDesign = useCallback(({ preview, sources }) => {
        const arts = [preview, ...(sources || [])];
        setSel((s) => {
            const uploadKey = (product?.option_groups.find((g) => g.type === "upload") || {}).key || "artwork";
            return { ...s, [uploadKey]: [...(s[uploadKey] || []), ...arts], design_preview: preview };
        });
    }, [product]);

    useEffect(() => {
        if (!product || !sel) return;
        const t = setTimeout(() => {
            api.post("/estimate", { product_slug: product.slug, selections: sel }).then(({ data }) => setEstimate(data)).catch(() => {});
        }, 250);
        return () => clearTimeout(t);
    }, [sel, product]);

    if (!product || !sel) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;

    const groups = product.option_groups;
    const addToCart = () => {
        setAdding(true);
        const item = {
            product_slug: product.slug, product_name: product.name, category: product.category,
            selections: sel, artwork: sel.artwork || [],
            quantity: estimate?.quantity || 1, unit_price: estimate?.unit_price || 0,
            line_total: estimate?.line_total || 0, image: product.image, summary: summarize(product, sel),
        };
        addItem(item);
        toast.success("Added to cart");
        setAdding(false);
        setOpen(true);
    };

    const buyNow = () => { addToCart(); navigate("/cart"); };

    const ModeToggle = () => (
        <div className="inline-flex rounded-full border border-white/15 bg-white/[0.03] p-1" data-testid="mode-toggle">
            <button onClick={() => setMode("wizard")} data-testid="mode-wizard" className={cx("flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors", mode === "wizard" ? "bg-brand-green text-black" : "text-zinc-300")}>
                <Wand2 size={15} /> Wizard
            </button>
            <button onClick={() => setMode("pro")} data-testid="mode-pro" className={cx("flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors", mode === "pro" ? "bg-brand-blue text-white" : "text-zinc-300")}>
                <LayoutGrid size={15} /> Pro Mode
            </button>
        </div>
    );

    const totalSteps = groups.length;
    const canNext = groupComplete(groups[step], sel);

    return (
        <Layout hideTicker>
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green">{product.tagline}</p>
                        <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">{product.name}</h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <button onClick={() => setShowDesigner(true)} data-testid="open-designer-button"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-green/50 bg-brand-green/10 px-4 py-2 text-sm font-bold text-brand-green transition-colors hover:bg-brand-green/20">
                            <Palette size={15} /> Live Designer
                        </button>
                        <ModeToggle />
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-12">
                    {/* Left: builder */}
                    <div className="lg:col-span-8">
                        {mode === "wizard" ? (
                            <div>
                                <div className="mb-6">
                                    <div className="mb-2 flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500">
                                        <span>Step {step + 1} of {totalSteps}</span><span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
                                    </div>
                                    <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                                        <motion.div className="h-full bg-brand-green" animate={{ width: `${((step + 1) / totalSteps) * 100}%` }} transition={{ duration: 0.4 }} />
                                    </div>
                                </div>
                                <AnimatePresence mode="wait">
                                    <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}
                                        className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
                                        <h2 className="font-display text-2xl font-bold">{groups[step].label}</h2>
                                        {groups[step].help && <p className="mt-1 mb-5 text-sm text-zinc-400">{groups[step].help}</p>}
                                        {groups[step].type === "upload" && (
                                            <button onClick={() => setShowDesigner(true)} data-testid="wizard-open-designer"
                                                className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-green/50 bg-brand-green/10 px-4 py-2 text-sm font-bold text-brand-green transition-colors hover:bg-brand-green/20">
                                                <Palette size={15} /> Or design it live →
                                            </button>
                                        )}
                                        <div className={groups[step].help ? "" : "mt-5"}><Field g={groups[step]} sel={sel} update={update} /></div>
                                    </motion.div>
                                </AnimatePresence>
                                <div className="mt-6 flex items-center justify-between">
                                    <GhostButton onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wizard-back"><ArrowLeft size={16} /> Back</GhostButton>
                                    {step < totalSteps - 1 ? (
                                        <GreenButton onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} data-testid="wizard-next">Next <ArrowRight size={16} /></GreenButton>
                                    ) : (
                                        <GreenButton onClick={buyNow} disabled={adding} data-testid="wizard-checkout">Add & Checkout <ArrowRight size={16} /></GreenButton>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-brand-green/30 bg-gradient-to-r from-brand-green/10 to-transparent p-5 sm:flex-row sm:items-center" data-testid="pro-designer-callout">
                                    <div>
                                        <h3 className="flex items-center gap-2 font-display text-lg font-bold"><Sparkles size={18} className="text-brand-green" /> Design it live</h3>
                                        <p className="mt-1 text-sm text-zinc-400">Drop in your art, add text, and place it right on the {product.category === "apparel" ? "shirt" : "sheet"} — signs365-style.</p>
                                    </div>
                                    <GreenButton onClick={() => setShowDesigner(true)} data-testid="pro-open-designer"><Palette size={16} /> Open Designer</GreenButton>
                                </div>
                                {groups.map((g) => (
                                    <div key={g.key} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`pro-group-${g.key}`}>
                                        <h3 className="font-display text-lg font-bold">{g.label}</h3>
                                        {g.help && <p className="mb-3 mt-0.5 text-xs text-zinc-500">{g.help}</p>}
                                        <div className="mt-3"><Field g={g} sel={sel} update={update} /></div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right: sticky summary */}
                    <div className="lg:col-span-4">
                        <div className="lg:sticky lg:top-24">
                            <SummaryPane product={product} sel={sel} estimate={estimate} onAdd={addToCart} adding={adding} />
                        </div>
                    </div>
                </div>
            </div>
            {showDesigner && <LiveDesigner product={product} onClose={() => setShowDesigner(false)} onUse={handleUseDesign} />}
        </Layout>
    );
}
